/**
 * Preferences. See docs/11-data-model.md Section 2.
 *
 * One record, read once and written on change. Kept apart from `entries` so a
 * full health-data wipe (FR-607) is a store deletion and does not take the
 * user's preferences with it — someone deleting their cycle history has not
 * asked to have their theme reset.
 */
import { openDb, promisify, STORE, txDone } from './db'

export const SETTINGS_KEY = 'settings'
export const SETTINGS_SCHEMA_VERSION = 1

export interface PremiumState {
  licenseKey: string
  activationId: string
  lastValidated: string
  status: 'active' | 'expired' | 'revoked'
}

export interface Settings {
  v: number
  /**
   * FR-412. Suspends every estimate: no predicted period, no fertile window,
   * no "you are late".
   *
   * `reason` is optional and is NEVER required. The app does not need to know
   * why, asking would be intrusive, and a stored reason is one more sensitive
   * fact sitting on the device. It exists only so the UI can remind the user
   * what they chose.
   */
  predictions: {
    suspended: boolean
    reason?: 'pregnancy' | 'postpartum' | 'menopause' | 'contraception' | 'other' | undefined
  }
  /** How many missing days may sit inside one period. See domain/cycles. */
  cycle: { maxGapDays: number }
  premium?: PremiumState | undefined
}

export const DEFAULT_SETTINGS: Settings = {
  v: SETTINGS_SCHEMA_VERSION,
  predictions: { suspended: false },
  cycle: { maxGapDays: 1 },
}

export async function loadSettings(): Promise<Settings> {
  const db = await openDb()
  const tx = db.transaction(STORE.settings, 'readonly')
  const stored = await promisify<Settings | undefined>(
    tx.objectStore(STORE.settings).get(SETTINGS_KEY),
  )
  if (stored === undefined) return { ...DEFAULT_SETTINGS }

  // Merged over the defaults rather than trusted wholesale, so a settings
  // record written by an older version is missing keys rather than breaking.
  return {
    ...DEFAULT_SETTINGS,
    ...stored,
    predictions: { ...DEFAULT_SETTINGS.predictions, ...stored.predictions },
    cycle: { ...DEFAULT_SETTINGS.cycle, ...stored.cycle },
    premium: stored.premium,
    v: SETTINGS_SCHEMA_VERSION,
  }
}

export async function saveSettings(settings: Settings): Promise<Settings> {
  const next: Settings = { ...settings, v: SETTINGS_SCHEMA_VERSION }
  const db = await openDb()
  const tx = db.transaction(STORE.settings, 'readwrite')
  tx.objectStore(STORE.settings).put(next, SETTINGS_KEY)
  await txDone(tx)
  return next
}

/**
 * Turn estimates off or back on.
 *
 * Clearing `suspended` also clears the reason: keeping a stale "pregnancy"
 * marker on a re-enabled account would leave a sensitive fact stored for no
 * purpose, which is the opposite of what this app is for.
 */
export async function setPredictionsSuspended(
  suspended: boolean,
  reason?: Settings['predictions']['reason'],
): Promise<Settings> {
  const current = await loadSettings()
  const predictions: Settings['predictions'] = { suspended }
  if (suspended && reason !== undefined) predictions.reason = reason
  return saveSettings({ ...current, predictions })
}
